import * as React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { LoadingState } from '@/components/app/LoadingState';
import { humanizeError } from '@/lib/apiClient';
import { useRecentDecisions } from '../hooks';
import { CRON_SCHEDULES, nextTickHint } from '../cronSchedules';
import { DECISION_OUTCOMES, outcomeBadgeVariant, type DecisionOutcome } from '../decisionOutcome';
import type { RecentDecision } from '../api/notificationsApi';

const IDLE_THRESHOLD_MS = 30 * 60_000;

type Filter = 'all' | DecisionOutcome | 'opened';

const FILTER_LABELS: Record<Filter, string> = {
  all: 'All',
  send: 'Sent',
  skip: 'Skipped',
  defer: 'Deferred',
  opened: 'Opened',
};

// Sanity check: keep the filter and the canonical outcome list in sync.
// If the backend ever adds a new outcome, this fails to type-check until
// FILTER_LABELS is updated.
const _typeCheckOutcomes: readonly Filter[] = DECISION_OUTCOMES;
void _typeCheckOutcomes;

interface DecisionsBrowserProps {
  /** Optional userId to scope every query to a single user. */
  userId?: string;
  /** Page size. Server clamps to 50. */
  pageSize?: number;
  /** Default filter on mount. */
  initialFilter?: Filter;
}

export function DecisionsBrowser({ userId, pageSize = 20, initialFilter = 'all' }: DecisionsBrowserProps) {
  const [filter, setFilter] = React.useState<Filter>(initialFilter);
  const [cursor, setCursor] = React.useState<string | undefined>(undefined);
  const [cursorStack, setCursorStack] = React.useState<string[]>([]);

  const params = React.useMemo(() => {
    const base: Parameters<typeof useRecentDecisions>[0] = { limit: pageSize };
    if (userId) base.userId = userId;
    if (filter === 'opened') base.opened = true;
    else if (filter !== 'all') base.outcome = filter;
    if (cursor) base.cursor = cursor;
    return base;
  }, [filter, cursor, userId, pageSize]);

  const onFirstPage = !cursor && cursorStack.length === 0;
  const query = useRecentDecisions(params);

  const decisions = query.data?.decisions ?? [];
  const nextCursor = query.data?.nextCursor ?? null;
  const hasPrev = cursorStack.length > 0;

  const handleFilter = (next: Filter) => {
    setFilter(next);
    setCursor(undefined);
    setCursorStack([]);
  };

  const handleNext = () => {
    if (!nextCursor) return;
    setCursorStack((stack) => [...stack, cursor ?? '']);
    setCursor(nextCursor);
  };

  const handlePrev = () => {
    if (cursorStack.length === 0) return;
    const prev = cursorStack[cursorStack.length - 1] || undefined;
    setCursor(prev);
    setCursorStack((stack) => stack.slice(0, -1));
  };

  const pageNumber = cursorStack.length + 1;

  // Idle banner only makes sense on the unfiltered first page — pagination
  // and filtered views show stale-by-design slices. Also gate on fetch
  // freshness: with polling removed the banner could otherwise sit there
  // claiming "system idle" hours after the user loaded the tab.
  const dataFreshMs = query.dataUpdatedAt ? Date.now() - query.dataUpdatedAt : Infinity;
  const showIdleBanner =
    onFirstPage && filter === 'all' && decisions.length > 0 && dataFreshMs < 5 * 60_000;
  const loadedAgoLabel = query.dataUpdatedAt
    ? formatRelative(new Date(query.dataUpdatedAt).toISOString())
    : null;

  return (
    <div className="space-y-2">
      {showIdleBanner && <IdleBanner decisions={decisions} />}
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 pt-3">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>Filter:</span>
          <Select value={filter} onValueChange={(v) => handleFilter(v as Filter)}>
            <SelectTrigger className="h-7 w-[140px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(FILTER_LABELS) as Filter[]).map((k) => (
                <SelectItem key={k} value={k} className="text-xs">
                  {FILTER_LABELS[k]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {loadedAgoLabel && (
            <span className="text-[11px] italic" title={`Loaded ${loadedAgoLabel}`}>
              · loaded {loadedAgoLabel}
            </span>
          )}
        </div>
        <span className="text-[11px] text-muted-foreground">Page {pageNumber}</span>
      </div>

      {query.isError ? (
        <div className="mx-4 my-3 flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs">
          <AlertTriangle className="h-3.5 w-3.5 text-destructive" />
          <span className="flex-1 text-destructive">{humanizeError(query.error)}</span>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setCursor(undefined);
              setCursorStack([]);
            }}
          >
            Reset
          </Button>
        </div>
      ) : query.isLoading && decisions.length === 0 ? (
        <LoadingState inline label="Loading decisions" />
      ) : decisions.length === 0 ? (
        <p className="px-4 py-6 text-center text-sm text-muted-foreground">
          {filter === 'all'
            ? 'No decisions yet — send a notification to see it appear here.'
            : `No ${FILTER_LABELS[filter].toLowerCase()} decisions in this window.`}
        </p>
      ) : (
        <DecisionsList decisions={decisions} />
      )}

      <div className="flex items-center justify-end gap-2 border-t px-4 py-2">
        <Button size="sm" variant="ghost" onClick={handlePrev} disabled={!hasPrev || query.isFetching}>
          <ChevronLeft className="h-3.5 w-3.5" /> Prev
        </Button>
        <Button size="sm" variant="ghost" onClick={handleNext} disabled={!nextCursor || query.isFetching}>
          Next <ChevronRight className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

function DecisionsList({ decisions }: { decisions: RecentDecision[] }) {
  return (
    <ul className="divide-y text-sm">
      {decisions.map((d) => (
        <li
          key={d.decisionId}
          className="grid grid-cols-[auto_1fr_auto] items-center gap-3 px-4 py-2 hover:bg-muted/40"
        >
          <Badge variant={outcomeBadgeVariant(d.outcome)}>{d.outcome}</Badge>
          <div className="min-w-0">
            <Link
              to={`/notifications/decisions/${encodeURIComponent(d.decisionId)}`}
              className="block min-w-0 hover:underline"
            >
              <div className="truncate text-xs">
                <span className="font-medium">{d.notificationType}</span>
                {d.category && <span className="text-muted-foreground"> · {d.category}</span>}
                {d.journeyId && <span className="text-muted-foreground"> · {d.journeyId}</span>}
              </div>
            </Link>
            <div className="truncate text-[11px] text-muted-foreground">
              <Link
                to={`/notifications/users/${encodeURIComponent(d.userId)}`}
                className="font-mono hover:underline"
              >
                {d.userId.slice(0, 8)}…
              </Link>
              {d.reasonCodes && d.reasonCodes.length > 0 && (
                <> · {d.reasonCodes.join(', ')}</>
              )}
            </div>
          </div>
          <div className="text-right text-[11px] text-muted-foreground">
            {formatRelative(d.decidedAt)}
            <div className="mt-0.5">{renderStatusBadge(d)}</div>
          </div>
        </li>
      ))}
    </ul>
  );
}

function renderStatusBadge(d: RecentDecision): React.ReactNode {
  // Opened beats sent/skipped/deferred — once a user has tapped, that's the
  // most useful state to surface in the row. Falls back to deliveryStatus
  // for sends that haven't been opened (yet), and shows nothing for
  // skips/defers without delivery info.
  if (d.opened) {
    // Distinct violet so an opened row is visually unmistakable next to the
    // green "sent" pill — important on the live-decisions list where opens
    // are sparse and easy to miss.
    return (
      <Badge
        variant="default"
        className="border-transparent bg-violet-500 text-white text-[10px] hover:bg-violet-500"
      >
        <CheckCircle2 className="mr-0.5 h-2.5 w-2.5" /> opened
      </Badge>
    );
  }
  if (!d.deliveryStatus) return null;
  const variant: 'success' | 'warning' | 'destructive' =
    d.deliveryStatus === 'sent'
      ? 'success'
      : d.deliveryStatus === 'partial'
        ? 'warning'
        : 'destructive';
  return (
    <Badge variant={variant} className="text-[10px]">
      {d.deliveryStatus}
    </Badge>
  );
}

function IdleBanner({ decisions }: { decisions: RecentDecision[] }) {
  const newest = decisions[0];
  if (!newest) return null;
  const ageMs = Date.now() - new Date(newest.decidedAt).getTime();
  if (!Number.isFinite(ageMs) || ageMs < IDLE_THRESHOLD_MS) return null;

  const idleMin = Math.round(ageMs / 60_000);
  const orchestrator = CRON_SCHEDULES.IntentOrchestratorCron;
  const hint = nextTickHint(orchestrator?.cadenceMinutes);

  return (
    <div className="mx-4 mt-4 rounded-md border border-dashed bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
      System is idle — last activity {idleMin}m ago.
      {hint && <> Orchestrator {hint}.</>}
    </div>
  );
}

function formatRelative(iso?: string): string {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return iso;
  if (ms < 60_000) return 'just now';
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.round(ms / 3_600_000)}h ago`;
  return `${Math.round(ms / 86_400_000)}d ago`;
}
